import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useState, useEffect, ReactElement } from 'react';
import LoadingIndicator from '@/components/LoadingIndicator/LoadingIndicator';
import { getScheduleTimes } from '@/lib/helpers/multi-livestream/getScheduleTimes';
import { SCHEDULE_WINDOW_IN_HOURS, SCHEDULE_WINDOW_IN_MINUTES } from '@/lib/helpers/multi-livestream/constants';
import { Channel } from '@/lib/types/api/multi-livestream';
import MultiLivestreamChannel from '@/components/MultiLivestream/Schedule/Channel';
import MultiLivestreamTimeline from '@/components/MultiLivestream/Schedule/Timeline';
import styles from '@/components/MultiLivestream/Schedule/Schedule.module.scss';
dayjs.extend(utc);
type MultiLivestreamScheduleProps = {
  selectedChannel: string;
  setUserSelectedChannel: React.Dispatch<React.SetStateAction<string>>;
  scheduleData: Channel[];
  primaryTimezone: string;
  secondaryTimezone?: string | null;
};
const MultiLivestreamSchedule = (props: MultiLivestreamScheduleProps): ReactElement => {
  const {
    selectedChannel,
    setUserSelectedChannel,
    scheduleData,
    primaryTimezone,
    secondaryTimezone
  } = props;
  const [scheduleTimes, setScheduleTimes] = useState<Dayjs[]>([]);
  // eslint-disable-next-line
  let nowOverride: Dayjs | null = null;
  // nowOerride is a developer convenience to stress test schedules,
  // - it allows you to dictate what "now" is on the schedule.
  // You need to set it to
  // - today's date (note timezone - it may need to be EST or EDT depending
  // on the time of year)
  // - sometime after the start of the current half-hour schedule window
  // - it can be several hours into the future, if needed
  // @TODO remove this override once the feature is settled and we
  // don't need it anymore

  // !!!! comment it out before commiting !!!
  // nowOverride = dayjs('Mon, 05 Feb 2024 10:29:07 EST');

  // The "now" state is very important, and drives the rest of the schedule
  const [now, setNow] = useState(nowOverride ? nowOverride : dayjs());
  // we update "now" every 1 minute
  // note - you can change 60 to another value, like 5,
  // to update every 5 seconds during development, which is helpful
  // when paired with nowOverride - this will essentially let you
  // 'fast forward' in time

  // !!!! set this to "60 * 1000" before commiting !!!
  const NOW_INTERVAL = 60 * 1000;
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(nowOverride ? now.add(1, 'minute') : dayjs());
    }, NOW_INTERVAL);
    return () => clearInterval(interval);
  }, [now, nowOverride, NOW_INTERVAL]);

  // update schedule times and percent complete on initial load and repeat every minute
  useEffect(() => {
    if (primaryTimezone) {
      setScheduleTimes(getScheduleTimes(now, primaryTimezone, SCHEDULE_WINDOW_IN_HOURS));
    }
  }, [primaryTimezone, now]);
  return <section className={styles.multi_livestream__schedule__container} data-sentry-component="MultiLivestreamSchedule" data-sentry-source-file="Schedule.tsx">
      <div className={styles.multi_livestream__schedule__wrapper}>
        {selectedChannel && scheduleData && scheduleTimes.length > 0 ? <div className={styles.multi_livestream__schedule} style={{
        gridTemplateColumns: `clamp(90px, 30vw + 1rem, 200px) repeat(${SCHEDULE_WINDOW_IN_MINUTES}, 1fr)`
      }}>
            <>
              <MultiLivestreamTimeline scheduleTimes={scheduleTimes} secondaryTimezone={secondaryTimezone} now={now} />
              {/* Channels */}
              {scheduleData.map((channel: Channel, channelIndex) => {
            return <MultiLivestreamChannel channel={channel} key={channelIndex} setUserSelectedChannel={setUserSelectedChannel} selectedChannel={selectedChannel} scheduleTimes={scheduleTimes} now={now} />;
          })}
            </>
          </div> : <div className={styles.multi_livestream__schedule__loading}>
            <LoadingIndicator />
          </div>}
      </div>
    </section>;
};
export default MultiLivestreamSchedule;